import { useMemo, useRef } from "react"
import { useFrame } from "@react-three/fiber"
import { Sparkles } from "@react-three/drei"
import * as THREE from "three"

import type { AvatarModel, AvatarPolarity } from "@domo/shared"
import { modelPalette } from "./modelPalette"

interface DescentBeamProps {
    /** Posición XZ del pad de aterrizaje. Y se ignora — el haz cae desde 20 → 0. */
    pad: [number, number, number]
    /** Modelo elegido: define el color del haz y los sparkles. */
    model: AvatarModel
    /** Polaridad elegida: modula el hue del haz y los sparkles. */
    polarity: AvatarPolarity
    /** performance.now() del momento que arrancó la animación local. */
    startedAt: number
    /** Duración del haz cayendo desde y=BEAM_TOP a y=0. */
    descentMs: number
    /** Duración de la materialización (sparkles + emerge avatar). */
    materializeMs: number
}

const BEAM_TOP = 20
const BEAM_RADIUS_TOP = 0.08
const BEAM_RADIUS_BOTTOM = 0.45

// Sprint 3 — Haz de luz vertical que cae desde BEAM_TOP hasta el pad
// del Tripulante. Tiene 3 fases visuales:
//   1. fase descent (0 → descentMs): el haz baja y la cabeza luminosa
//      deja una estela detrás. Al final del intervalo la cabeza
//      impacta el pad.
//   2. fase impact (descentMs → descentMs+150ms): pulse de aro
//      expansivo + intensidad máxima del haz.
//   3. fase materialize (descentMs+150 → descentMs+materializeMs):
//      sparkles densas + el haz se desvanece progresivamente. El
//      avatar lo materializa el componente padre con su propio
//      timer (mostrando opacity/scale en transición).
//
// Después de descentMs+materializeMs el componente debería desmontarse;
// internamente clamp para que aunque siga montado no haga nada.
export function DescentBeam({
    pad,
    model,
    polarity,
    startedAt,
    descentMs,
    materializeMs,
}: DescentBeamProps) {
    const headRef = useRef<THREE.Mesh>(null)
    const beamRef = useRef<THREE.Mesh>(null)
    const beamMatRef = useRef<THREE.MeshBasicMaterial>(null)
    const ringRef = useRef<THREE.Mesh>(null)
    const ringMatRef = useRef<THREE.MeshBasicMaterial>(null)

    const palette = useMemo(
        () => modelPalette(model, polarity),
        [model, polarity]
    )

    const totalMs = descentMs + materializeMs

    useFrame(() => {
        const elapsed = performance.now() - startedAt
        if (elapsed > totalMs) return
        const tDescent = Math.min(1, elapsed / descentMs)
        // Easing: head accelerates as it falls (cubic).
        const headY = BEAM_TOP * (1 - tDescent * tDescent * tDescent)

        if (headRef.current) {
            headRef.current.position.set(pad[0], headY, pad[2])
            const breathing = 1 + Math.sin(elapsed * 0.012) * 0.1
            headRef.current.scale.setScalar(
                tDescent < 1 ? breathing : 0.5 + (1 - tDescent) * 0.5
            )
        }

        // Beam goes from headY (top) down to 0. The cylinder is
        // centered between, so its position.y = headY/2 and height = headY.
        if (beamRef.current && beamMatRef.current) {
            const h = Math.max(0.001, headY)
            beamRef.current.position.set(pad[0], h / 2, pad[2])
            beamRef.current.scale.set(1, h / BEAM_TOP, 1)
            // El beam se torna más brillante mientras desciende; al
            // impacto pulsa fuerte; en materializing se desvanece.
            const elapsedAfterImpact = Math.max(0, elapsed - descentMs)
            const fade =
                elapsedAfterImpact > 0
                    ? Math.max(0, 1 - elapsedAfterImpact / materializeMs)
                    : 1
            beamMatRef.current.opacity = 0.55 * fade + 0.15 * tDescent
        }

        // Anillo de impacto: aparece al tocar el pad y se expande.
        if (ringRef.current && ringMatRef.current) {
            const tImpact =
                tDescent >= 1
                    ? Math.min(
                          1,
                          (elapsed - descentMs) / Math.min(450, materializeMs)
                      )
                    : 0
            ringRef.current.position.set(pad[0], 0.02, pad[2])
            const r = 0.4 + tImpact * 4.2
            ringRef.current.scale.set(r, r, r)
            ringMatRef.current.opacity = (1 - tImpact) * 0.85
        }
    })

    return (
        <group>
            {/* Cabeza luminosa que desciende */}
            <mesh ref={headRef}>
                <sphereGeometry args={[0.55, 24, 24]} />
                <meshBasicMaterial
                    color={palette.head}
                    transparent
                    opacity={0.92}
                    depthWrite={false}
                />
            </mesh>

            {/* Pilar de luz / haz vertical: cono finito que va de la
                cabeza hasta el suelo. Origen apunta al cielo cuando
                rotation = 0; lo escalamos en Y según altura actual. */}
            <mesh ref={beamRef}>
                <cylinderGeometry
                    args={[
                        BEAM_RADIUS_TOP,
                        BEAM_RADIUS_BOTTOM,
                        BEAM_TOP,
                        24,
                        1,
                        true,
                    ]}
                />
                <meshBasicMaterial
                    ref={beamMatRef}
                    color={palette.beam}
                    transparent
                    opacity={0.5}
                    depthWrite={false}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* Anillo de impacto en el pad — expansivo. */}
            <mesh
                ref={ringRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[pad[0], 0.02, pad[2]]}
            >
                <ringGeometry args={[0.95, 1, 64]} />
                <meshBasicMaterial
                    ref={ringMatRef}
                    color={palette.beam}
                    transparent
                    opacity={0}
                    depthWrite={false}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* Luz puntual emisiva del haz — proyecta color en el suelo. */}
            <pointLight
                position={[pad[0], 1.2, pad[2]]}
                intensity={2.4}
                distance={9}
                color={palette.beam}
            />

            {/* Sparkles que detonan al impacto del haz contra el pad. */}
            <Sparkles
                count={42}
                scale={[3.2, 1.8, 3.2]}
                position={[pad[0], 0.6, pad[2]]}
                size={6}
                speed={0.7}
                color={palette.spark}
                opacity={0.95}
            />
        </group>
    )
}
